import { useMemo } from 'react';
import { NO_UPSELL_SEEN, type UpsellFacts } from 'fym-shared/src/music24/watermarkUpsell';
import { useLicenseInfo } from '../../../redux/slices/license/hooks/hooks';
import { useIsDemoAccount } from '../../accountSelector/useIsDemoAccount';
import { useOnboardingState } from '../../onboarding/useOnboardingState';
import { useMyTeamRoleStatus } from '../../team/useMyTeamRole';
import { useMusic24Plan } from '../hooks/useMusic24Plan';
import { useIsNativeEmbed } from '../navigation/hooks/useIsNativeEmbed';
import { upsellSeenOf } from './upsellSeenOf';
import { useWatermarkSummary } from './useWatermarkSummary';

export interface UpsellFactsState {
  facts: UpsellFacts;
  /**
   * True when the role, the summary and the one-time flags are answers of the server. An automatic
   * modal waits for it: a default must never open one.
   */
  isReady: boolean;
}

/**
 * The facts that the rules of `fym-shared/src/music24/watermarkUpsell.ts` read: the plan, the role,
 * the demo artist, the native web view, the count of watermarked files, the trial days and the
 * one-time flags of the user.
 */
export function useUpsellFacts(): UpsellFactsState {
  const plan = useMusic24Plan();
  const role = useMyTeamRoleStatus();
  const isDemo = useIsDemoAccount();
  const isNativeEmbed = useIsNativeEmbed();
  const licenseInfo = useLicenseInfo();
  const { summary, isLoaded } = useWatermarkSummary();
  const { state } = useOnboardingState();
  const seen = useMemo(() => upsellSeenOf(state), [state]);
  const trialDaysRemaining = plan === 'trial' ? licenseInfo.trialDaysRemaining : null;

  return useMemo(
    () => ({
      facts: {
        plan,
        isOwner: role.role === 'OWNER',
        isDemo,
        isNativeEmbed,
        files: summary.files,
        trialDaysRemaining,
        seen: seen ?? NO_UPSELL_SEEN,
      },
      isReady: role.isResolved && isLoaded && seen !== null,
    }),
    [plan, role.role, role.isResolved, isDemo, isNativeEmbed, summary.files, trialDaysRemaining, seen, isLoaded]
  );
}
